import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import isEqual from 'lodash/isEqual';
import some from 'lodash/some';

import {
  Breadcrumbs,
  DatePickerV2,
  DateRangePickerV2,
  HelpIcon,
  OverlayTriggerTooltip,
  Radio,
  Select,
} from '@utilities';

import { getTooltipContent } from './utilities/helpers';

import CustomDateGroups from './components/CustomDateGroups';

import styles from './_index.module.scss';

const DateOptions = ({
  activeAnswers,
  activePrompt,
  dateOptions,
  isLoading,
  onSelectAnswer,
  results,
}) => {
  const [customDates, setCustomDates] = useState([null, null]);
  const [isCustomChecked, setIsCustomChecked] = useState(false);
  const [isCustomPointForwardChecked, setIsCustomPointForwardChecked] = useState(false);

  const isAnswerInOptions = (dateOption) =>
    some(dateOption?.options, (option) => isEqual(option?.value, activeAnswers));
  const [selectedTimeframe, setSelectedTimeframe] = useState(
    dateOptions?.find((dateOption) => isAnswerInOptions(dateOption))?.timeFrame
  );

  const [startDate, endDate] = customDates;

  const customDateGroups = results?.filter((result) => result?.id?.includes('date_group'));

  const handleRadioChange = (answer) => {
    setSelectedTimeframe(answer?.timeFrame);
    onSelectAnswer(answer?.options[0]?.value);
    setIsCustomChecked(false);
    setIsCustomPointForwardChecked(false);
  };

  const handleSelectChange = (answer) => {
    onSelectAnswer(answer?.value);
  };

  const renderDateOptionItem = (dateOption, index) => {
    const options = dateOption?.options;
    const isChecked =
      !isCustomChecked &&
      !isCustomPointForwardChecked &&
      selectedTimeframe === dateOption?.timeFrame &&
      isAnswerInOptions(dateOption);
    const value = {
      label: options?.find((option) => isEqual(option?.value, activeAnswers))?.label,
      value: activeAnswers,
    };

    return (
      <li key={`date-option-${index}`}>
        <Radio
          isChecked={isChecked}
          label={dateOption?.timeFrame}
          onChange={handleRadioChange}
          value={dateOption}
        />
        {isChecked && options?.length > 1 && (
          <div className={styles['date-options-select-container']}>
            <Select onChange={handleSelectChange} options={options} value={value} />
            <OverlayTriggerTooltip content={getTooltipContent(dateOption?.type)} placement="right">
              <HelpIcon />
            </OverlayTriggerTooltip>
          </div>
        )}
      </li>
    );
  };

  const { pointForwardOptions, regularOptions } = useMemo(() => {
    const pointForward = [];
    const regular = [];

    dateOptions?.forEach((dateOption) => {
      if (dateOption?.options.length > 0) {
        const valueLength = dateOption.options[0]?.value.length;
        if (valueLength === 1) {
          pointForward.push(dateOption);
        } else if (valueLength > 1) {
          regular.push(dateOption);
        }
      }
    });

    return {
      pointForwardOptions: pointForward,
      regularOptions: regular,
    };
  }, [dateOptions]);

  useEffect(() => {
    const hasDateOptionAnswer = some(
      dateOptions?.reduce((a, b) => a.concat(b?.options), []),
      (option) => isEqual(option?.value, activeAnswers)
    );
    const hasCustomDateAnswer =
      activeAnswers &&
      Array.isArray(activeAnswers) &&
      activeAnswers?.length === 2 &&
      !hasDateOptionAnswer;
    const hasCustomPointForwardAnswer =
      activeAnswers &&
      Array.isArray(activeAnswers) &&
      activeAnswers?.length === 1 &&
      typeof activeAnswers[0] === 'string' &&
      !hasDateOptionAnswer;

    if (
      activeAnswers &&
      (hasCustomDateAnswer ||
        (activePrompt?.dateOptions?.length === 0 &&
          activePrompt?.hasCustomDateOption &&
          !activeAnswers?.[0]?.id?.includes('date_group')))
    ) {
      setCustomDates(activeAnswers);
      setIsCustomChecked(true);
      setIsCustomPointForwardChecked(false);
    } else if (
      activeAnswers &&
      (hasCustomPointForwardAnswer ||
        (activePrompt?.dateOptions?.length === 0 &&
          activePrompt?.hasCustomDateOption &&
          !activeAnswers?.[0]?.id?.includes('date_group')))
    ) {
      setCustomDates(activeAnswers);
      setIsCustomChecked(false);
      setIsCustomPointForwardChecked(true);
    } else {
      setCustomDates([null, null]);
      setIsCustomChecked(false);
      setIsCustomPointForwardChecked(false);
    }
  }, [activePrompt]);

  useEffect(() => {
    if (!activeAnswers) {
      setCustomDates([null, null]);
    }
  }, [activeAnswers]);

  useEffect(() => {
    if (isCustomChecked && !(customDates.includes(null) || customDates.includes(undefined))) {
      onSelectAnswer(customDates);
    } else if (isCustomPointForwardChecked && customDates?.length > 0 && customDates[0]) {
      onSelectAnswer(customDates[0]);
    }
  }, [customDates, isCustomChecked, isCustomPointForwardChecked]);

  return (
    <Row className={styles['date-options']}>
      <Col xs={3}>
        <ul className={styles['date-options-list']}>
          {regularOptions.map((dateOption, index) => renderDateOptionItem(dateOption, index))}
          {activePrompt?.hasCustomDateOption && (
            <li>
              <Radio
                isChecked={isCustomChecked}
                label="Custom Time Period"
                name={activePrompt?.id}
                onChange={() => {
                  setIsCustomChecked(true);
                  setIsCustomPointForwardChecked(false);
                }}
              />
            </li>
          )}
        </ul>
        {pointForwardOptions.length > 0 && (
          <>
            <Breadcrumbs options={[{ label: 'Point Forward Period' }]} />
            <ul className={styles['date-options-list']}>
              {pointForwardOptions.map((dateOption, index) =>
                renderDateOptionItem(dateOption, index)
              )}
              <li>
                <Radio
                  isChecked={isCustomPointForwardChecked}
                  label="Custom Point Forward Period"
                  name={activePrompt?.id}
                  onChange={() => {
                    setIsCustomPointForwardChecked(true);
                    setIsCustomChecked(false);
                  }}
                />
              </li>
            </ul>
          </>
        )}

        <Breadcrumbs options={[{ label: 'Custom' }]} />
        <CustomDateGroups
          activeAnswers={activeAnswers}
          customDateGroups={customDateGroups}
          isCustomChecked={isCustomChecked}
          isLoading={isLoading}
          onSelectAnswer={onSelectAnswer}
          setIsCustomChecked={setIsCustomChecked}
        />
      </Col>
      <Col xs={9}>
        {isCustomChecked && (
          <DateRangePickerV2
            endDate={endDate}
            maxDate={activePrompt?.maxDate}
            onDatesChange={setCustomDates}
            startDate={startDate}
          />
        )}
        {isCustomPointForwardChecked && (
          <DatePickerV2
            date={startDate}
            maxDate={activePrompt?.maxDate}
            onDateChange={(selectedDate) => {
              setCustomDates([selectedDate, null]);
            }}
          />
        )}
      </Col>
    </Row>
  );
};

DateOptions.propTypes = {
  activeAnswers: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  activePrompt: PropTypes.object,
  dateOptions: PropTypes.array,
  isLoading: PropTypes.bool,
  onSelectAnswer: PropTypes.func,
  results: PropTypes.array,
};

export default DateOptions;
